import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Mail } from 'lucide-react';
import { emailVerificationUtils } from '../utils/pendingProject';
import { authService } from '../services/authService';
import { grpcClientFactory } from '../services/grpcClientFactory';
import { SignupConsentFields } from './SignupConsentFields';
import { buildConsentWordingVersions } from '../constants/consent';
import { getReferralCode } from '../utils/referralCode';
// Type-safe imports to avoid TypeScript definition issues
const protoModule = require('../../_proto/landscope-map-viewer_pb');
export const AccountModal = ({ isOpen, onClose, onSwitchToLogin, onEmailVerificationSet, initialData }) => {
    const [formData, setFormData] = useState({
        firstName: (initialData === null || initialData === void 0 ? void 0 : initialData.firstName) || '',
        lastName: (initialData === null || initialData === void 0 ? void 0 : initialData.lastName) || '',
        email: (initialData === null || initialData === void 0 ? void 0 : initialData.email) || ''
    });
    const [consent, setConsent] = useState({ marketingEmail: false, adAudience: false });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [isRedirecting, setIsRedirecting] = useState(false);
    // Post-signup "check your inbox" state. Rather than flashing a message and
    // auto-closing, the modal holds here so a user whose email is delayed or in
    // spam can always resend without needing a working link.
    const [verifyPending, setVerifyPending] = useState(false);
    const [pendingEmail, setPendingEmail] = useState('');
    const [isResending, setIsResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendMessage, setResendMessage] = useState('');
    // Update form data when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                firstName: initialData.firstName || '',
                lastName: initialData.lastName || '',
                email: initialData.email || ''
            }));
        }
    }, [initialData]);
    // When the modal is (re)opened and a verification is already pending, drop
    // straight into the "check your inbox" state. This is what the "Verify your
    // email" re-entry button in the account panel lands on.
    useEffect(() => {
        if (isOpen && emailVerificationUtils.isPending()) {
            const email = emailVerificationUtils.getEmail();
            if (email) {
                setPendingEmail(email);
                setVerifyPending(true);
            }
        }
    }, [isOpen]);
    // Resend cooldown countdown.
    useEffect(() => {
        if (resendCooldown <= 0)
            return;
        const timer = setInterval(() => {
            setResendCooldown(prev => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);
    const handleResend = async () => {
        if (isResending || resendCooldown > 0 || !pendingEmail)
            return;
        setIsResending(true);
        setResendMessage('');
        try {
            const result = await authService.resendVerificationEmail(pendingEmail);
            if (result.success) {
                setResendMessage('Sent — check your inbox (and your spam folder).');
                setResendCooldown(30);
            }
            else {
                setResendMessage(result.message || 'Could not resend. Please try again.');
            }
        }
        catch (err) {
            console.error('Failed to resend verification email:', err);
            setResendMessage('Could not resend. Please try again.');
        }
        finally {
            setIsResending(false);
        }
    };
    const handleChangeEmail = () => {
        // Return to the form so a typo'd address can be corrected without starting
        // over. Keep the address pre-filled for editing.
        emailVerificationUtils.clear();
        setFormData(prev => ({ ...prev, email: pendingEmail }));
        setVerifyPending(false);
        setResendMessage('');
        setResendCooldown(0);
    };
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
        // Clear Google loading state if user interacts with form
        if (isGoogleLoading) {
            setIsGoogleLoading(false);
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        }
        else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setIsSubmitting(true);
        setErrors({});
        try {
            // Factory client: it sends the attribution cookies CreateUser stamps
            // onto the new user. A hand-built client drops them cross-origin.
            const client = grpcClientFactory.createUserClient();
            // Create request using the proto module
            const CreateUserRequest = protoModule.CreateUserRequest;
            const request = new CreateUserRequest();
            request.setFirstName(formData.firstName.trim());
            request.setLastName(formData.lastName.trim());
            request.setEmail(formData.email.trim().toLowerCase());
            request.setMarketingEmailConsent(consent.marketingEmail);
            request.setAdAudienceConsent(consent.adAudience);
            request.setConsentWordingVersions(buildConsentWordingVersions(protoModule));
            request.setReferralCode(getReferralCode());
            // Make the gRPC call
            const response = await new Promise((resolve, reject) => {
                client.createUser(request, {}, (err, response) => {
                    if (err) {
                        console.error('Create user error:', err);
                        reject(err);
                    }
                    else {
                        resolve(response);
                    }
                });
            });
            // Check the actual response content
            const success = response.getSuccess();
            const message = response.getMessage();
            if (success) {
                const email = formData.email.trim().toLowerCase();
                // Persist the pending state + email so resend works without the link.
                emailVerificationUtils.setPending(email);
                // Hold the modal in the "check your inbox" state instead of auto-closing,
                // so a user whose email is delayed/in spam can always resend.
                setPendingEmail(email);
                setVerifyPending(true);
                setResendMessage('');
                // Email was just sent — start the cooldown before the first resend.
                setResendCooldown(30);
                // Notify parent that email verification was set (to clear drawing interface)
                if (onEmailVerificationSet) {
                    onEmailVerificationSet();
                }
            }
            else {
                // Check if it's a "user already exists" message
                if (message && message.toLowerCase().includes('already exists')) {
                    setSuccessMessage("Your account already exists. Redirecting to login...");
                    setIsRedirecting(true);
                    // Redirect to login after 3 seconds
                    setTimeout(() => {
                        setSuccessMessage('');
                        setIsRedirecting(false);
                        onClose();
                        if (onSwitchToLogin) {
                            onSwitchToLogin();
                        }
                    }, 3000);
                }
                else {
                    // Other failure cases - check for domain restriction messages
                    if (message && message.includes('Email domain not authorized')) {
                        setErrors({ email: message });
                    }
                    else {
                        setErrors({ general: message || 'Failed to create account. Please try again.' });
                    }
                }
            }
        }
        catch (error) {
            console.error('Error creating user:', error);
            // Handle different types of errors
            if (error.code === 6) { // ALREADY_EXISTS
                setErrors({ email: 'An account with this email already exists' });
            }
            else if (error.code === 3) { // INVALID_ARGUMENT
                setErrors({ general: 'Invalid input data. Please check your information.' });
            }
            else {
                setErrors({ general: 'Failed to create account. Please try again.' });
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleGoogleLogin = async () => {
        setIsGoogleLoading(true);
        setErrors(prev => ({ ...prev, general: '' }));
        try {
            // Start external login and redirect to provider
            const url = await authService.startExternalLogin('GOOGLE', getReferralCode());
            window.location.href = url;
        }
        catch (err) {
            console.error('Failed to start Google login', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to start Google login';
            setErrors(prev => ({ ...prev, general: errorMessage }));
            setIsGoogleLoading(false);
        }
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-[70] flex items-center justify-center p-4", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: onClose }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative w-full max-w-md flex flex-col", style: { maxHeight: '92vh' }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: [_jsx("h2", { className: "text-xl font-semibold", style: { color: 'var(--sand)' }, children: verifyPending ? 'Check your inbox' : 'Create Account' }), _jsx("button", { onClick: onClose, className: "p-1 rounded hover:bg-opacity-20 hover:bg-white transition-colors", style: { color: 'var(--sand)' }, children: _jsx(X, { className: "w-5 h-5 sm:w-6 sm:h-6" }) })] }), _jsx("div", { className: "p-6 overflow-y-auto min-h-0 flex-1 ls-modal-band-bottom", style: { backgroundColor: 'var(--sand)' }, children: verifyPending ? (
                        /* Post-signup "check your inbox" state */
                        _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full", style: { backgroundColor: 'var(--forest)' }, children: _jsx(Mail, { className: "w-7 h-7", style: { color: 'var(--sand)' } }) }), _jsx("p", { className: "text-sm mb-1", style: { color: 'var(--charcoal)' }, children: "We sent a verification link to" }), _jsx("p", { className: "text-base font-semibold mb-4 break-all", style: { color: 'var(--charcoal)' }, children: pendingEmail }), _jsx("p", { className: "text-sm mb-5", style: { color: 'var(--charcoal)' }, children: "Click the link in that email to finish setting up your account." }), resendMessage && (_jsx("div", { className: "mb-4 p-3 rounded-lg bg-green-50 border border-green-200", children: _jsx("p", { className: "text-green-700 text-sm", children: resendMessage }) })), _jsxs("div", { className: "rounded-lg p-4 mb-4 text-left", style: { backgroundColor: 'white', border: '1px solid var(--pewter)' }, children: [_jsx("p", { className: "text-sm mb-3", style: { color: 'var(--charcoal)' }, children: "Didn't get it? Check your spam folder, or:" }), _jsx("button", { type: "button", onClick: handleResend, disabled: isResending || resendCooldown > 0, className: "w-full py-2.5 px-4 rounded-full font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: isResending
                                                ? 'Sending…'
                                                : resendCooldown > 0
                                                    ? `Resend in ${resendCooldown}s`
                                                    : 'Resend verification email' })] }), _jsx("button", { type: "button", onClick: handleChangeEmail, className: "text-sm font-medium hover:underline", style: { color: 'var(--forest)' }, children: "Wrong email? Change it" })] })) : (_jsxs(_Fragment, { children: [_jsx("button", { onClick: handleGoogleLogin, disabled: isGoogleLoading || isSubmitting, className: "w-full flex items-center justify-center gap-3 p-3 mb-6 border rounded-lg hover:bg-opacity-10 hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed", style: {
                                        borderColor: 'var(--pewter)',
                                        backgroundColor: 'white',
                                        color: 'var(--charcoal)'
                                    }, children: isGoogleLoading ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" }), _jsx("span", { className: "font-medium", children: "Connecting to Google..." })] })) : (_jsxs(_Fragment, { children: [_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", children: [_jsx("path", { fill: "#4285F4", d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" }), _jsx("path", { fill: "#34A853", d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" }), _jsx("path", { fill: "#FBBC05", d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" }), _jsx("path", { fill: "#EA4335", d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" })] }), _jsx("span", { className: "font-medium", children: "Log in with Google" })] })) }), _jsxs("div", { className: "relative mb-6", children: [_jsx("div", { className: "absolute inset-0 flex items-center", children: _jsx("div", { className: "w-full border-t", style: { borderColor: 'var(--pewter)' } }) }), _jsx("div", { className: "relative flex justify-center text-sm", children: _jsx("span", { className: "px-2 bg-white", style: {
                                                    backgroundColor: 'var(--sand)',
                                                    color: 'var(--charcoal)'
                                                }, children: "or" }) })] }), errors.general && (_jsx("div", { className: "mb-4 p-3 rounded-lg bg-red-50 border border-red-200", children: _jsx("p", { className: "text-red-600 text-sm", children: errors.general }) })), successMessage && (_jsxs("div", { className: "mb-4 p-4 rounded-lg bg-green-50 border border-green-200", children: [_jsx("p", { className: "text-green-700 text-sm text-center font-medium", children: successMessage }), isRedirecting && (_jsx("div", { className: "mt-2 flex justify-center", children: _jsx("div", { className: "animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent" }) }))] })), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "firstName", className: "block text-sm font-medium mb-1", style: { color: 'var(--charcoal)' }, children: "First Name" }), _jsx("input", { type: "text", id: "firstName", value: formData.firstName, onChange: (e) => handleInputChange('firstName', e.target.value), className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 ${errors.firstName ? 'border-red-500' : ''}`, style: {
                                                                borderColor: errors.firstName ? '#ef4444' : 'var(--pewter)',
                                                                backgroundColor: 'white',
                                                                color: 'var(--charcoal)'
                                                            }, placeholder: "First Name", disabled: isSubmitting || !!successMessage }), errors.firstName && (_jsx("p", { className: "text-red-500 text-xs mt-1", children: errors.firstName }))] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "lastName", className: "block text-sm font-medium mb-1", style: { color: 'var(--charcoal)' }, children: "Last Name" }), _jsx("input", { type: "text", id: "lastName", value: formData.lastName, onChange: (e) => handleInputChange('lastName', e.target.value), className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 ${errors.lastName ? 'border-red-500' : ''}`, style: {
                                                                borderColor: errors.lastName ? '#ef4444' : 'var(--pewter)',
                                                                backgroundColor: 'white',
                                                                color: 'var(--charcoal)'
                                                            }, placeholder: "Last Name", disabled: isSubmitting || !!successMessage }), errors.lastName && (_jsx("p", { className: "text-red-500 text-xs mt-1", children: errors.lastName }))] })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "email", className: "block text-sm font-medium mb-1", style: { color: 'var(--charcoal)' }, children: "Email" }), _jsx("input", { type: "email", id: "email", value: formData.email, onChange: (e) => handleInputChange('email', e.target.value), className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 ${errors.email ? 'border-red-500' : ''}`, style: {
                                                        borderColor: errors.email ? '#ef4444' : 'var(--pewter)',
                                                        backgroundColor: 'white',
                                                        color: 'var(--charcoal)'
                                                    }, placeholder: "your@email.com", disabled: isSubmitting || !!successMessage }), errors.email && (_jsx("p", { className: "text-red-500 text-xs mt-1", children: errors.email }))] }), _jsx(SignupConsentFields, { value: consent, onChange: setConsent, disabled: isSubmitting || !!successMessage }), _jsx("button", { type: "submit", disabled: isSubmitting || !!successMessage, className: `w-full py-3 px-4 rounded-full font-medium transition-colors ${(isSubmitting || !!successMessage) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`, style: {
                                                backgroundColor: 'var(--forest)',
                                                color: 'var(--sand)'
                                            }, children: isSubmitting ? 'Creating Account...' :
                                                successMessage ? 'Processing...' : 'Create Account' }), _jsx("div", { className: "text-center mt-4", children: _jsxs("span", { className: "text-sm", style: { color: 'var(--pewter)' }, children: ["Already have an account?", ' ', _jsx("button", { type: "button", onClick: () => {
                                                            onClose();
                                                            if (onSwitchToLogin) {
                                                                onSwitchToLogin();
                                                            }
                                                        }, disabled: isSubmitting || !!successMessage, className: "font-medium hover:underline transition-colors", style: {
                                                            color: 'var(--forest)',
                                                            cursor: (isSubmitting || !!successMessage) ? 'not-allowed' : 'pointer'
                                                        }, children: "Sign in" })] }) })] })] })) })] })] }));
};
